import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Info, LogIn, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FullPageSpinner } from '@/components/FullPageSpinner';
import {
  useAuth,
  useSessionState,
  AccountDisabledError,
  MFA_ENROLL_PATH,
  MFA_REPLACE_AUTHENTICATOR_PATH,
  parseSignInReason,
  type SecondFactorChallenge,
} from '@/auth';
import { SecondFactorChallengeForm } from './mfa/SecondFactorChallengeForm';
import { landingRouteFor } from '@/routes/roleRoutes';
import { clearReturnPath, peekReturnPath, rememberReturnPath } from '@/auth/returnTo';
import { AuthNetworkError, InvalidCredentialsError, TooManyAttemptsError } from '@/auth/authErrors';
import { messages } from '@/i18n';
import { AuthScreenLayout } from './AuthScreenLayout';

const INPUT_CLASS =
  'border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2';

function messageFor(error: unknown): string {
  if (error instanceof InvalidCredentialsError) return messages.signIn.errors.invalidCredentials;
  if (error instanceof TooManyAttemptsError) return messages.signIn.errors.tooManyAttempts;
  if (error instanceof AuthNetworkError) return messages.signIn.errors.network;
  return messages.signIn.errors.unavailable;
}

/**
 * The route they originally asked for, if it survived the round trip, and the
 * role's own landing route otherwise. RoleGuard still has the final say on the
 * remembered path — this is navigation, not authorisation.
 *
 * A component rather than a branch inline in SignIn, for two reasons. The path
 * has to be read after sign-in resolves rather than at mount, because it is
 * written on submit. And the read must be captured once and cleared separately:
 * StrictMode renders twice, so a read that cleared as it went would lose the
 * path on the second pass, in development only.
 */
function ReturnToOrLanding({ fallback }: { fallback: string }) {
  const [to] = useState(() => peekReturnPath() ?? fallback);
  useEffect(clearReturnPath, []);
  return <Navigate to={to} replace />;
}

export function SignIn() {
  const { isAuthenticated, signIn } = useAuth();
  const { status, session } = useSessionState();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(null);
  const reason = parseSignInReason(location.search);

  // Already signed in and resolved: send them where they belong rather than
  // rendering a sign-in form that would start a second flow.
  if (isAuthenticated && status === 'ready' && session) {
    return <ReturnToOrLanding fallback={landingRouteFor(session.role)} />;
  }

  // Sign-in succeeded and /me answered "no". Neither state is resolvable from
  // this screen, and neither is handled anywhere else: /sign-in sits outside
  // SessionGuard on purpose, so nothing downstream picks them up. Falling
  // through to the form instead leaves it disabled, silent and with no way
  // forward. The unprovisioned case in particular is the normal outcome of
  // invite-only onboarding, not an edge case — see AccessNotProvisioned.
  if (isAuthenticated && status === 'not-provisioned') {
    return <Navigate to="/access-not-provisioned" replace />;
  }
  if (isAuthenticated && status === 'error') {
    return <Navigate to="/auth-error" replace />;
  }
  // The same reasoning for the two MFA refusals: signed in, but the API wants
  // a factor enrolled (or TOTP re-enrolled after an SMS sign-in) first.
  if (isAuthenticated && status === 'mfa-enrollment-required') {
    return <Navigate to={MFA_ENROLL_PATH} replace />;
  }
  if (isAuthenticated && status === 'mfa-reenrollment-required') {
    return <Navigate to={MFA_REPLACE_AUTHENTICATOR_PATH} replace />;
  }

  // signInWithEmailAndPassword resolved with no reload, but the /me round trip
  // is still in flight. Render the spinner here, or the form flashes back up
  // fully populated for that duration — which reads as "it didn't work" and
  // gets the button pressed twice.
  if (isAuthenticated && status === 'loading') {
    return <FullPageSpinner label={messages.common.loading} />;
  }

  // No auto-redirect/auto-submit on mount. Auto-redirecting makes sign-out
  // land the user straight back in sign-in, and turns the back button into a
  // trap.
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // AuthGuard put the requested path here when it bounced the user out.
    const from = (location.state as { from?: string } | null)?.from;
    rememberReturnPath(from);

    setPending(true);
    signIn({ email, password })
      .then((result) => {
        // Password accepted, second factor next. The provider is still
        // signed out, so nothing above redirects until the challenge is done.
        if (result.status === 'second-factor-required') {
          setChallenge(result.challenge);
          setPending(false);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof AccountDisabledError) {
          void navigate('/access-not-provisioned', { replace: true });
          return;
        }
        setError(messageFor(err));
        setPending(false);
      });
  };

  if (challenge) {
    return (
      <AuthScreenLayout title={messages.mfa.challengeTitle}>
        <SecondFactorChallengeForm
          challenge={challenge}
          cancelLabel={messages.mfa.backToPassword}
          onCancel={() => {
            setChallenge(null);
            setPassword('');
          }}
        />
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout title={messages.signIn.title} description={messages.signIn.subtitle}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {reason && !error ? (
          <Alert>
            <Info aria-hidden />
            <AlertDescription>{messages.mfa.signInReasons[reason]}</AlertDescription>
          </Alert>
        ) : null}

        {error ? (
          <Alert variant="destructive">
            <TriangleAlert aria-hidden />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          {messages.signIn.emailLabel}
          <input
            type="email"
            required
            autoComplete="username"
            className={INPUT_CLASS}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          {messages.signIn.passwordLabel}
          <input
            type="password"
            required
            autoComplete="current-password"
            className={INPUT_CLASS}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={pending}
          />
        </label>

        <Button type="submit" className="w-full" disabled={pending}>
          <LogIn aria-hidden />
          {messages.signIn.submit}
        </Button>

        <Link
          to="/forgot-password"
          className="text-muted-foreground text-center text-xs hover:underline"
        >
          {messages.signIn.forgotPassword}
        </Link>
      </form>
    </AuthScreenLayout>
  );
}
